import { Gift, Clock, Check, Trophy, ArrowRight, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * ProfileUnlockStep — the post-signup interstitial that invites a freshly
 * authenticated user to complete their demographic profile for bonus jackpot
 * entries ("Unlock +N more entries!" with "Let's Go!" / "Skip → Dashboard").
 *
 * Extracted from LemonadeOnboarding (REK-1730 QA) so BOTH the regular signup
 * flow AND the one-click sign-up flow (`AuthSignup.tsx`, after auto-login) can
 * render the same screen. It is purely presentational — the caller wires
 * `onStartWizard` (open the LemonadeProfileWizard) and `onSkip` (go to the
 * dashboard). It is shown only to authenticated users (the account exists by
 * the time this renders, in both flows).
 */
export function ProfileUnlockStep({
  remainingEntries,
  onStartWizard,
  onSkip,
}: {
  remainingEntries: number;
  onStartWizard: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="text-center space-y-8 py-8 opacity-0 animate-fade-up">
      {/* Animated gift icon. The positioning wrapper is sized to the icon
          (w-24, mx-auto) so the entry badge below anchors to the GIFT's
          top-right corner -- not the full-width content area (REK-1730 QA). */}
      <div className="relative w-24 h-24 mx-auto">
        <div className="w-24 h-24 rounded-2xl bg-linear-to-br from-primary/20 to-primary/5 flex items-center justify-center animate-bounce-subtle">
          <Gift className="w-12 h-12 text-primary" />
        </div>
        {/* Entry badge -- snug pill (width hugs the number) straddling the
            icon's right edge: right-0 puts its right edge at the icon edge and
            translate-x-1/2 shifts it out by half its width, so the middle of
            the text sits on the edge of the gift's light background. */}
        <div className="absolute -top-2 right-0 translate-x-1/2 bg-primary text-primary-foreground text-sm font-bold px-2.5 py-1 rounded-full shadow-lg">
          +{remainingEntries}
        </div>
      </div>

      <div className="space-y-3">
        <h1 className="text-3xl lg:text-4xl font-medium text-foreground">
          Unlock +{remainingEntries} more entries!
        </h1>
        <p className="text-lg text-muted-foreground max-w-md mx-auto">
          Answer a few quick questions about yourself to boost your jackpot chances
        </p>
      </div>

      {/* Benefits */}
      <div className="flex flex-wrap justify-center gap-3 text-sm">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-muted-foreground">
          <Clock className="w-4 h-4" />
          ~2 minutes
        </span>
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-muted-foreground">
          <Check className="w-4 h-4" />
          Save anytime
        </span>
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-medium">
          <Trophy className="w-4 h-4" />
          +{remainingEntries} entries
        </span>
      </div>

      <div className="space-y-3 pt-4">
        <Button
          type="button"
          size="lg"
          onClick={onStartWizard}
          className="w-full h-14 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
        >
          Let's Go!
          <ArrowRight className="w-5 h-5" />
        </Button>

        <Button
          type="button"
          variant="ghost"
          onClick={onSkip}
          className="w-full text-muted-foreground text-sm hover:text-foreground"
        >
          <Wallet className="w-4 h-4" />
          Skip for now → Go to Dashboard
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        You can always complete this later from your dashboard
      </p>
    </div>
  );
}
